import { useEffect, useLayoutEffect, useRef } from "react";

/**
 * Tap-through guard for touch overlays.
 *
 * Closing an overlay (search "Cancel", a sheet's x, the + menu, a morph
 * window...) with a finger can leave the rest of that gesture to land on
 * whatever was under it: the overlay is gone (or `pointer-events: none`
 * while it animates out) by the time the pointerup / touchend / click (or
 * iOS's delayed "ghost" click after the keyboard goes down) arrives, so
 * the calendar button or a list row under it fires too.
 *
 * `guardTapThrough()` swallows those events, in the capture phase of the
 * window, for a short while. It only arms when the closing gesture was a
 * touch or a pen (a mouse never produces ghost clicks), so desktop and
 * keyboard dismissals are untouched.
 */

/** How long after a touch close the page under it ignores taps. */
export const TAP_GUARD_MS = 350;
/** A touch this recent is the one that closed the overlay. */
const RECENT_TOUCH_MS = 1000;

const SWALLOWED_EVENTS = [
  "pointerdown",
  "pointerup",
  "mousedown",
  "mouseup",
  "touchend",
  "click",
] as const;

let lastTouchAt = Number.NEGATIVE_INFINITY;
let tracking = false;
let releaseTimer: number | undefined;

const now = (): number => performance.now();

const rememberTouch = (event: Event) => {
  // A mouse (or an unknown pointer type) never produces ghost clicks.
  if (
    event instanceof PointerEvent &&
    event.pointerType !== "touch" &&
    event.pointerType !== "pen"
  ) {
    return;
  }
  lastTouchAt = now();
};

/** Starts noting touch presses (once per page), so a close can tell its input. */
export const trackTouches = (): void => {
  if (tracking || typeof window === "undefined") return;
  tracking = true;
  window.addEventListener("pointerdown", rememberTouch, { capture: true });
  window.addEventListener("touchstart", rememberTouch, {
    capture: true,
    passive: true,
  });
};

const swallow = (event: Event) => {
  // preventDefault on touchend also cancels iOS's synthesized mouse events
  // and click; on click it stops a link from navigating.
  if (event.cancelable) event.preventDefault();
  event.stopImmediatePropagation();
};

const release = () => {
  window.clearTimeout(releaseTimer);
  releaseTimer = undefined;
  for (const type of SWALLOWED_EVENTS) {
    window.removeEventListener(type, swallow, { capture: true });
  }
  delete document.documentElement.dataset.tapGuard;
};

/** Whether the guard is currently swallowing taps. */
export const isTapGuardActive = (): boolean => releaseTimer !== undefined;

/**
 * Swallows the rest of the closing gesture and any tap in the next
 * `duration` ms. No-op unless the last press was a recent touch, or when
 * `force` is set. Returns a function that lifts the guard early.
 */
export const guardTapThrough = ({
  duration = TAP_GUARD_MS,
  force = false,
}: { duration?: number; force?: boolean } = {}): (() => void) => {
  if (typeof window === "undefined") return () => {};
  if (!force && now() - lastTouchAt > RECENT_TOUCH_MS) return () => {};
  if (releaseTimer === undefined) {
    for (const type of SWALLOWED_EVENTS) {
      window.addEventListener(type, swallow, { capture: true });
    }
  }
  window.clearTimeout(releaseTimer);
  releaseTimer = window.setTimeout(release, duration);
  document.documentElement.dataset.tapGuard = "";
  return release;
};

/** Test helper: forget the last touch and lift any running guard. */
export const resetTapThroughGuard = (): void => {
  lastTouchAt = Number.NEGATIVE_INFINITY;
  release();
};

/**
 * Arms the tap-through guard whenever `open` goes from true to false.
 * Runs in a layout effect so it is in place before the browser handles the
 * next event of the gesture that closed the overlay.
 */
export const useTapThroughGuard = (open: boolean): void => {
  const wasOpen = useRef(open);
  useEffect(() => {
    trackTouches();
  }, []);
  useLayoutEffect(() => {
    if (wasOpen.current && !open) guardTapThrough();
    wasOpen.current = open;
  }, [open]);
};
